// Canvas renderer for the aerial plan export (PNG / PDF / share) — mirrors the on-screen 2D board.
import { MATERIAL_BY_NAME, shapeArea } from '@/lib/planMaterials';
import { getTexture, EDGES, inkFor } from '@/lib/planTextures';

const LEGEND_H = 64;

function pattern(ctx, kind, color, pxPerM, ox = 0, oy = 0) {
  const t = getTexture(kind, color);
  const p = ctx.createPattern(t.canvas, 'repeat');
  p.setTransform(new DOMMatrix().translate(ox, oy).scale((t.tileM * pxPerM) / t.canvas.width));
  return p;
}

function shapePath(ctx, sh, w, h, pxPerM, inset = 0) {
  ctx.beginPath();
  if (sh.type === 'poly' && sh.points?.length >= 3) {
    sh.points.forEach((p, i) => { const px = (p.x - sh.w / 2) * pxPerM, py = (p.y - sh.h / 2) * pxPerM; if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py); });
    ctx.closePath(); return;
  }
  if (sh.type === 'circle') { ctx.ellipse(0, 0, Math.max(1, w / 2 - inset), Math.max(1, h / 2 - inset), 0, 0, Math.PI * 2); return; }
  const iw = w - inset * 2, ih = h - inset * 2;
  const r = Math.max(0, Math.min(((sh.curve || 0) * Math.min(w, h)) / 2 - inset, Math.min(iw, ih) / 2));
  if (!r) { ctx.rect(-iw / 2, -ih / 2, iw, ih); return; }
  ctx.moveTo(-iw / 2 + r, -ih / 2);
  ctx.arcTo(iw / 2, -ih / 2, iw / 2, ih / 2, r); ctx.arcTo(iw / 2, ih / 2, -iw / 2, ih / 2, r);
  ctx.arcTo(-iw / 2, ih / 2, -iw / 2, -ih / 2, r); ctx.arcTo(-iw / 2, -ih / 2, iw / 2, -ih / 2, r);
  ctx.closePath();
}

function arrowLine(ctx, x1, y1, x2, y2, a) {
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  const ang = Math.atan2(y2 - y1, x2 - x1);
  [[x1, y1, ang + Math.PI], [x2, y2, ang]].forEach(([x, y, t]) => {
    ctx.beginPath(); ctx.moveTo(x, y);
    ctx.lineTo(x - a * Math.cos(t - 0.45), y - a * Math.sin(t - 0.45));
    ctx.lineTo(x - a * Math.cos(t + 0.45), y - a * Math.sin(t + 0.45)); ctx.closePath(); ctx.fill();
  });
}

// Small architectural dimension lines along the top and left inside edges
export function drawDims(ctx, w, h, wLabel, hLabel, color, k, inset = 0) {
  if (w < 60 * k || h < 56 * k) return;
  ctx.save();
  ctx.translate(inset, inset); w -= inset * 2; h -= inset * 2;
  ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = 0.9 * k;
  ctx.font = `500 ${10 * k}px Manrope, Arial, sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  const pad = 6 * k, ty = -h / 2 + 11 * k, tw = ctx.measureText(wLabel).width + 8 * k;
  arrowLine(ctx, -w / 2 + pad, ty, -tw / 2, ty, 4 * k); arrowLine(ctx, w / 2 - pad, ty, tw / 2, ty, 4 * k);
  ctx.fillText(wLabel, 0, ty);
  const lx = -w / 2 + 11 * k, th = ctx.measureText(hLabel).width + 8 * k;
  arrowLine(ctx, lx, -h / 2 + 22 * k, lx, -th / 2, 4 * k); arrowLine(ctx, lx, h / 2 - pad, lx, th / 2, 4 * k);
  ctx.translate(lx, 0); ctx.rotate(-Math.PI / 2); ctx.fillText(hLabel, 0, 0);
  ctx.restore();
}

// North arrow + metre scale bar, drawn at (x, y) = bottom-right anchor
export function drawScale(ctx, x, y, pxPerM, k, ink = '#1f2937', alt = '#ffffff') {
  const len = [1, 2, 5, 10].find((m) => m * pxPerM >= 90 * k) || 10;
  const L = len * pxPerM;
  ctx.save(); ctx.fillStyle = ink; ctx.strokeStyle = ink; ctx.lineWidth = k;
  for (let i = 0; i < 4; i++) { ctx.fillStyle = i % 2 ? alt : ink; ctx.fillRect(x - L + (i * L) / 4, y - 6 * k, L / 4, 6 * k); }
  ctx.strokeRect(x - L, y - 6 * k, L, 6 * k);
  ctx.fillStyle = ink; ctx.font = `500 ${9 * k}px Manrope, Arial, sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
  ctx.fillText('0', x - L, y - 8 * k); ctx.fillText(`${len} m`, x, y - 8 * k);
  const nx = x - L - 30 * k, ny = y - 4 * k;
  ctx.beginPath(); ctx.moveTo(nx, ny - 26 * k); ctx.lineTo(nx + 7 * k, ny); ctx.lineTo(nx, ny - 6 * k); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(nx, ny - 26 * k); ctx.lineTo(nx - 7 * k, ny); ctx.lineTo(nx, ny - 6 * k); ctx.closePath(); ctx.stroke();
  ctx.font = `700 ${9 * k}px Manrope, Arial, sans-serif`; ctx.textAlign = 'right'; ctx.textBaseline = 'middle'; ctx.fillText('N', nx - 10 * k, ny - 16 * k);
  ctx.restore();
}

function titleBlock(ctx, { y, W, h, k, pxPerM, plotW, plotL, meta }) {
  ctx.fillStyle = '#0e1a2b'; ctx.fillRect(0, y, W, h);
  ctx.fillStyle = '#b08d57'; ctx.fillRect(0, y, W, 2 * k);
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffffff'; ctx.font = `600 ${15 * k}px Fraunces, Georgia, serif`;
  ctx.fillText(meta.title || 'Garden layout plan', 14 * k, y + h * 0.38);
  ctx.fillStyle = '#cbd5e1'; ctx.font = `500 ${10 * k}px Manrope, Arial, sans-serif`;
  const sub = [meta.client, `Plot ${plotW} × ${plotL} m`, meta.date || new Date().toLocaleDateString('en-GB')].filter(Boolean).join('  ·  ');
  ctx.fillText(sub, 14 * k, y + h * 0.72);
  ctx.textAlign = 'right'; ctx.fillStyle = '#b08d57'; ctx.font = `600 ${9 * k}px Manrope, Arial, sans-serif`;
  ctx.font = `600 ${7 * k}px Manrope, Arial, sans-serif`; ctx.fillText(meta.brand || 'DRAWN WITH TRADESMATE', W - 14 * k, y + h * 0.84);
  drawScale(ctx, W - 14 * k, y + h * 0.55, pxPerM, k, '#ffffff', '#0e1a2b');
}

// Site photo drawn like CSS object-cover, then zoomed and shifted (dx/dy in % of the board)
export function drawPhoto(ctx, o, W, H) {
  const im = o.img, s = Math.max(W / im.width, H / im.height) * (o.zoom || 1);
  ctx.drawImage(im, (W - im.width * s) / 2 + ((o.dx || 0) / 100) * W, (H - im.height * s) / 2 + ((o.dy || 0) / 100) * H, im.width * s, im.height * s);
}

function baLabel(ctx, t, x, y, k, right = false) {
  ctx.save(); ctx.font = `800 ${12 * k}px Manrope, Arial, sans-serif`; ctx.textBaseline = 'top';
  const w = ctx.measureText(t).width + 16 * k, x0 = right ? x - w : x;
  ctx.fillStyle = 'rgba(15,23,42,0.85)'; ctx.fillRect(x0, y, w, 24 * k);
  ctx.fillStyle = '#ffffff'; ctx.textAlign = 'left'; ctx.fillText(t, x0 + 8 * k, y + 6 * k); ctx.restore();
}

// Side-by-side "before" (photo only) and "after" (photo + plan) image for sending to the customer
export function renderBeforeAfter(opts) {
  const { boardW, plotW, plotL, overlay, k = 2 } = opts;
  const W = boardW * k, H = Math.max(120, (boardW / plotW) * plotL) * k, gap = 16 * k;
  return new Promise((resolve) => {
    const after = new Image();
    after.onload = () => {
      const cv = document.createElement('canvas'); cv.width = W * 2 + gap; cv.height = H;
      const ctx = cv.getContext('2d'); ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, cv.width, cv.height);
      ctx.save(); ctx.beginPath(); ctx.rect(0, 0, W, H); ctx.clip(); drawPhoto(ctx, overlay, W, H); ctx.restore();
      ctx.drawImage(after, 0, 0, W, H, W + gap, 0, W, H);
      baLabel(ctx, 'BEFORE', 12 * k, 12 * k, k); baLabel(ctx, 'AFTER', W + gap + 12 * k, 12 * k, k);
      resolve(cv.toDataURL('image/jpeg', 0.92));
    };
    after.src = renderPlan({ ...opts, overlay: { ...overlay, split: 0 } });
  });
}

export function renderPlan({ shapes, plotW, plotL, boardW, ground, realistic, showDims, fmtL, fmtA, meta = {}, overlay = null, k = 2 }) {
  const pxPerM = (boardW / plotW) * k;
  const W = boardW * k, H = Math.max(120, (boardW / plotW) * plotL) * k;
  const used = [...new Set(shapes.map((s) => s.name))];
  const cv = document.createElement('canvas');
  const ctx0 = cv.getContext('2d');
  ctx0.font = `500 ${11 * k}px Manrope, Arial, sans-serif`;
  // lay the legend out in rows that fit the plan width
  const legend = []; let lx = 14 * k, row = 0;
  used.forEach((name) => {
    const wItem = 44 * k + 8 * k + ctx0.measureText(name).width + 28 * k;
    if (lx + wItem > W && lx > 14 * k) { row += 1; lx = 14 * k; }
    legend.push({ name, x: lx, row }); lx += wItem;
  });
  const legendH = used.length ? (row + 1) * LEGEND_H * k * 0.6 + 16 * k : 0, TB = 54 * k;
  cv.width = W; cv.height = H + legendH + TB;
  const ctx = cv.getContext('2d');
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, cv.width, cv.height);

  if (realistic && ground !== 'plain') { ctx.fillStyle = pattern(ctx, ground, null, pxPerM); ctx.fillRect(0, 0, W, H); }
  else {
    ctx.fillStyle = '#f8fafc'; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1;
    for (let gx = 0; gx <= plotW; gx++) { ctx.beginPath(); ctx.moveTo(gx * pxPerM, 0); ctx.lineTo(gx * pxPerM, H); ctx.stroke(); }
    for (let gy = 0; gy <= plotL; gy++) { ctx.beginPath(); ctx.moveTo(0, gy * pxPerM); ctx.lineTo(W, gy * pxPerM); ctx.stroke(); }
  }
  if (overlay?.img) drawPhoto(ctx, overlay, W, H);
  ctx.globalAlpha = overlay ? overlay.opacity : 1;

  [...shapes].sort((a, b) => (a.z || 0) - (b.z || 0)).forEach((sh) => {
    const mat = MATERIAL_BY_NAME[sh.name] || {};
    const w = sh.w * pxPerM, h = sh.h * pxPerM;
    const fill = realistic ? pattern(ctx, mat.tex, sh.color, pxPerM, -w / 2, -h / 2) : sh.color;
    ctx.save();
    ctx.translate((sh.x + sh.w / 2) * pxPerM, (sh.y + sh.h / 2) * pxPerM);
    ctx.rotate(((sh.rot || 0) * Math.PI) / 180);
    if (realistic && (sh.z || 0) >= 0.3) { ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowOffsetX = 3 * k; ctx.shadowOffsetY = 4 * k; ctx.shadowBlur = 5 * k; }
    if ((mat.wall || mat.canBow) && sh.bow) {
      ctx.strokeStyle = fill; ctx.lineWidth = h; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(-w / 2, 0); ctx.quadraticCurveTo(0, -sh.bow * w / 2, w / 2, 0); ctx.stroke();
    } else {
      const edge = EDGES.find((e) => e.id === sh.edge && e.m);
      const tx = realistic && getTexture(mat.tex, sh.color);
      if (tx?.stretch) {
        ctx.drawImage(tx.canvas, -w / 2, -h / 2, w, h);
        ctx.shadowColor = 'transparent';
      } else {
      shapePath(ctx, sh, w, h, pxPerM);
      ctx.fillStyle = edge ? (realistic ? pattern(ctx, edge.id === 'brick' ? 'brick' : edge.id, edge.color || '#9c4a3a', pxPerM, -w / 2, -h / 2) : '#64748b') : fill;
      ctx.fill();
      ctx.shadowColor = 'transparent';
      if (edge && sh.type !== 'poly') { shapePath(ctx, sh, w, h, pxPerM, edge.m * pxPerM); ctx.fillStyle = fill; ctx.fill(); }
      if (edge && sh.type === 'poly') { ctx.fillStyle = fill; shapePath(ctx, sh, w, h, pxPerM); ctx.fill(); ctx.strokeStyle = realistic ? pattern(ctx, edge.id === 'brick' ? 'brick' : edge.id, edge.color, pxPerM) : '#64748b'; ctx.lineWidth = edge.m * pxPerM * 2; ctx.save(); ctx.clip(); ctx.stroke(); ctx.restore(); }
      }
      if (realistic && mat.roof) {
        ctx.strokeStyle = 'rgba(20,22,26,0.7)'; ctx.lineWidth = 2.5 * k; ctx.beginPath();
        const a = Math.min(w, h) / 2, X = w / 2, Y = h / 2;
        if (w >= h) { ctx.moveTo(-X, -Y); ctx.lineTo(-X + a, 0); ctx.lineTo(X - a, 0); ctx.lineTo(X, -Y); ctx.moveTo(-X, Y); ctx.lineTo(-X + a, 0); ctx.moveTo(X - a, 0); ctx.lineTo(X, Y); }
        else { ctx.moveTo(-X, -Y); ctx.lineTo(0, -Y + a); ctx.lineTo(0, Y - a); ctx.lineTo(-X, Y); ctx.moveTo(X, -Y); ctx.lineTo(0, -Y + a); ctx.moveTo(0, Y - a); ctx.lineTo(X, Y); }
        ctx.stroke();
      }
    }
    ctx.shadowColor = 'transparent';
    const thin = h < 16 * k;
    const object = mat.tree || getTexture(mat.tex, sh.color).stretch;
    const ink = thin || object ? '#1f2937' : inkFor(mat.tex, sh.color, realistic);
    const edgeIn = (EDGES.find((e) => e.id === sh.edge && e.m)?.m || 0) * pxPerM;
    if (showDims && !mat.wall && !object && sh.type !== 'poly' && sh.type !== 'circle') drawDims(ctx, w, h, fmtL(sh.w), fmtL(sh.h), ink, k, edgeIn);
    const text = (t, y) => {
      ctx.lineWidth = 3 * k; ctx.lineJoin = 'round';
      ctx.strokeStyle = ink === '#ffffff' ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.75)';
      ctx.strokeText(t, 0, y); ctx.fillStyle = ink; ctx.fillText(t, 0, y);
    };
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = `600 ${10 * k}px Manrope, Arial, sans-serif`;
    const label = mat.wall ? `${sh.name} · ${fmtL(Math.max(sh.w, sh.h))}` : sh.name.toUpperCase();
    text(label, thin ? -h / 2 - 8 * k : object ? h / 2 + 8 * k : (mat.wall ? 0 : -3 * k));
    if (!mat.wall && !object) { ctx.font = `500 ${9 * k}px Manrope, Arial, sans-serif`; text(sh.type === 'circle' && showDims ? `Ø ${fmtL(sh.w)} · ${fmtA(shapeArea(sh))}` : fmtA(shapeArea(sh)), 10 * k); }
    ctx.restore();
  });
  ctx.globalAlpha = 1;
  if (overlay?.img && overlay.split > 0) {
    const sx = (W * overlay.split) / 100;
    ctx.save(); ctx.beginPath(); ctx.rect(0, 0, sx, H); ctx.clip(); drawPhoto(ctx, overlay, W, H); ctx.restore();
    ctx.fillStyle = '#ffffff'; ctx.fillRect(sx - k, 0, 2 * k, H);
    baLabel(ctx, 'BEFORE', 10 * k, 10 * k, k); baLabel(ctx, 'AFTER', W - 10 * k, 10 * k, k, true);
  }
  ctx.strokeStyle = '#475569'; ctx.lineWidth = 2 * k; ctx.strokeRect(0, 0, W, H);

  if (used.length) {
    ctx.fillStyle = '#f8f8f6'; ctx.fillRect(0, H, W, cv.height - H);
    ctx.font = `500 ${11 * k}px Manrope, Arial, sans-serif`; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
    legend.forEach(({ name, x, row }) => {
      const mat = MATERIAL_BY_NAME[name] || {}; const sw = 44 * k, sh = 22 * k, y = H + (row + 0.5) * LEGEND_H * k * 0.6 + 8 * k;
      const lt = realistic && getTexture(mat.tex, mat.color);
      ctx.fillStyle = lt && !lt.stretch ? pattern(ctx, mat.tex, mat.color, pxPerM) : '#e2e8e0';
      ctx.fillRect(x, y - sh / 2, sw, sh);
      if (lt?.stretch) ctx.drawImage(lt.canvas, x + sw / 2 - sh / 2, y - sh / 2, sh, sh); ctx.strokeStyle = '#cbd5e1'; ctx.lineWidth = k; ctx.strokeRect(x, y - sh / 2, sw, sh);
      ctx.fillStyle = '#334155'; ctx.fillText(name, x + sw + 8 * k, y);
    });
  }
  titleBlock(ctx, { y: H + legendH, W, h: TB, k, pxPerM, plotW, plotL, meta });
  return cv.toDataURL('image/png');
}
